import React, {useCallback, useState} from 'react';
import {modelMetadataDeleteOption} from '../../../../../../store/modelMetadata';
import {useDispatch} from 'react-redux';
import {tid} from '../../../../../../testUtils';
import {messageAdd, MessageTypes} from '../../../../../../store/message';
import {useMounted} from '../../../../../../hooks/useMounted';
import {trackModelMetadataOptionDeleted} from '../../../../../../tracking/tracking';
import UsageCountTitle from '../updateOption/UsageCountTitle';
import UsageList from '../updateOption/UsageList';
import {ManageMetadataActionProps} from '../ActionMenuTypes';
import {CloseOutlined, DeleteOutlined} from '@ant-design/icons';
import {Checkbox, Button} from 'antd';
import styles from './DeleteOption.module.scss';

const DeleteOption: React.FC<ManageMetadataActionProps> = ({
  property,
  value,
  onSuccess,
  onCancel,
  refreshOptionsCount,
  fileTagCount,
}) => {
  const dispatch = useDispatch();
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const mounted = useMounted();

  const handleSubmit = useCallback(() => {
    if (!agree) return;
    setSubmitting(true);
    return modelMetadataDeleteOption(dispatch, property, value)
      .then(() => {
        dispatch(messageAdd(`The option has been deleted`, MessageTypes.SUCCESS));
        trackModelMetadataOptionDeleted();
        onSuccess(null);
        if (refreshOptionsCount) refreshOptionsCount();
      })
      .catch(() => null)
      .finally(() => {
        mounted.current && setSubmitting(false);
      });
  }, [dispatch, onSuccess, property, value, mounted, refreshOptionsCount, agree]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>Delete Option</h2>
        </div>
        <button className={styles.closeBtn} onClick={onCancel} aria-label="Close">
          <CloseOutlined />
        </button>
      </div>
      <div className={styles.formWrapper}>
        <p className={styles.subtitle}>
          <UsageCountTitle property={property} value={value} fileTagCount={fileTagCount} />
        </p>

        <div className={styles.confirmSection}>
          <Checkbox
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            disabled={submitting}
            className={styles.confirmCheckbox}
            {...tid('checkbox', 'delete-confirm')}
          >
            I confirm to delete this Option permanently.
          </Checkbox>
        </div>

        <div className={styles.buttonsContainer}>
          <Button onClick={onCancel} disabled={submitting} className={styles.cancelBtn}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!agree || submitting}
            icon={<DeleteOutlined />}
            className={styles.submitBtn}
            {...tid('btn-manage-metadata-delete')}
          >
            Delete Option
          </Button>
        </div>

        <UsageList property={property} value={value} fileTagCount={fileTagCount} />
      </div>
    </div>
  );
};

export default DeleteOption;
